import type {
  AdminInvite,
  AdminOrg,
  AdminUser,
  AdminUserDetail,
  AuditVerify,
  ImpersonationStarted,
  InviteCreate,
  InviteCreated,
  Page,
  PlatformAuditEventDto,
  PlatformUser,
  StaffMember,
} from '@fruuit/shared'
import { useState } from 'react'
import type { QueryParams } from '../../api/client'
import { api } from '../../api/client'
import type { MutationContext } from '../../api/hooks'
import { useApiQuery } from '../../api/hooks'

/** Admin panel endpoints (M-01, M-04). Every list is cursor-paginated (`{ items, next_cursor }`). */

export const adminKeys = {
  orgs: ['admin', 'orgs'] as const,
  users: ['admin', 'users'] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  invites: ['admin', 'invites'] as const,
  staff: ['admin', 'staff'] as const,
  platformUsers: ['admin', 'platform-users'] as const,
  audit: ['admin', 'audit'] as const,
}

/** A stack of cursors for Previous / Next over a keyset-paginated list. */
export function useCursorPager() {
  const [stack, setStack] = useState<string[]>([])
  const cursor = stack[stack.length - 1]
  return {
    cursor,
    page: stack.length + 1,
    hasPrevious: stack.length > 0,
    next: (nextCursor: string | null) => {
      if (nextCursor) setStack((s) => [...s, nextCursor])
    },
    previous: () => setStack((s) => s.slice(0, -1)),
    reset: () => setStack([]),
  }
}

function clean(query: QueryParams): QueryParams {
  const out: QueryParams = {}
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') out[k] = v
  return out
}

export function useAdminOrgs(query: QueryParams) {
  const q = clean(query)
  return useApiQuery<Page<AdminOrg>>([...adminKeys.orgs, q], '/admin/orgs', { query: q })
}

export function useAdminUsers(query: QueryParams) {
  const q = clean(query)
  return useApiQuery<Page<AdminUser>>([...adminKeys.users, q], '/admin/users', { query: q })
}

export function useAdminUser(id: string | null) {
  return useApiQuery<AdminUserDetail>(adminKeys.user(id ?? 'none'), `/admin/users/${id ?? ''}`, {
    enabled: id !== null,
  })
}

export function useStaff() {
  return useApiQuery<{ org_id: string | null; items: StaffMember[] }>(
    adminKeys.staff,
    '/admin/staff',
  )
}

export function usePlatformUsers() {
  return useApiQuery<{ items: PlatformUser[] }>(adminKeys.platformUsers, '/admin/platform-users')
}

export function useAdminAudit(query: QueryParams) {
  const q = clean(query)
  return useApiQuery<Page<PlatformAuditEventDto>>([...adminKeys.audit, q], '/admin/audit', {
    query: q,
  })
}

export function useAuditVerify() {
  return useApiQuery<AuditVerify>([...adminKeys.audit, 'verify'], '/admin/audit/verify')
}

const opts = (ctx: MutationContext) => ({ requestId: ctx.requestId })

export const adminApi = {
  createOrg: (body: { name: string; slug: string }, ctx: MutationContext) =>
    api.post<AdminOrg>('/admin/orgs', body, opts(ctx)),
  updateOrg: (
    vars: { id: string; revision: number; name?: string; slug?: string },
    ctx: MutationContext,
  ) => {
    const { id, ...body } = vars
    return api.patch(`/admin/orgs/${id}`, body, opts(ctx))
  },
  suspendOrg: (vars: { id: string; revision: number; reason: string }, ctx: MutationContext) =>
    api.post(
      `/admin/orgs/${vars.id}/suspend`,
      { revision: vars.revision, reason: vars.reason },
      opts(ctx),
    ),
  resumeOrg: (vars: { id: string; revision: number }, ctx: MutationContext) =>
    api.post(`/admin/orgs/${vars.id}/resume`, { revision: vars.revision }, opts(ctx)),

  invite: (body: InviteCreate, ctx: MutationContext) =>
    api.post<InviteCreated>('/admin/invites', body, opts(ctx)),
  resendInvite: (id: string, ctx: MutationContext) =>
    api.post<{ invite: AdminInvite; email_sent: boolean }>(
      `/admin/invites/${id}/resend`,
      undefined,
      opts(ctx),
    ),
  revokeInvite: (id: string, ctx: MutationContext) =>
    api.post<{ invite: AdminInvite }>(`/admin/invites/${id}/revoke`, undefined, opts(ctx)),

  setRole: (
    vars: { userId: string; org_id: string; role: 'investor' | 'investor_admin' | null },
    ctx: MutationContext,
  ) =>
    api.post(
      `/admin/users/${vars.userId}/role`,
      { org_id: vars.org_id, role: vars.role },
      opts(ctx),
    ),
  deactivate: (vars: { userId: string; revision: number; reason: string }, ctx: MutationContext) =>
    api.post(
      `/admin/users/${vars.userId}/deactivate`,
      { revision: vars.revision, reason: vars.reason },
      opts(ctx),
    ),
  reactivate: (vars: { userId: string; revision: number }, ctx: MutationContext) =>
    api.post(`/admin/users/${vars.userId}/reactivate`, { revision: vars.revision }, opts(ctx)),
  revokeSessions: (userId: string, ctx: MutationContext) =>
    api.post<{ ok: true; revoked: number }>(
      `/admin/users/${userId}/revoke-sessions`,
      undefined,
      opts(ctx),
    ),
  resetMfa: (vars: { userId: string; revision: number }, ctx: MutationContext) =>
    api.post(`/admin/users/${vars.userId}/reset-mfa`, { revision: vars.revision }, opts(ctx)),

  setStaffRoles: (
    vars: { userId: string; manager: boolean; reviewer: boolean },
    ctx: MutationContext,
  ) =>
    api.put(
      `/admin/staff/${vars.userId}`,
      { manager: vars.manager, reviewer: vars.reviewer },
      opts(ctx),
    ),
  setOperator: (vars: { userId: string; operator: boolean }, ctx: MutationContext) =>
    api.put(
      `/admin/platform-users/${vars.userId}/operator`,
      { operator: vars.operator },
      opts(ctx),
    ),

  impersonate: (vars: { user_id: string; reason: string }, ctx: MutationContext) =>
    api.post<ImpersonationStarted>('/admin/impersonate', vars, opts(ctx)),
}
